import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useThemeStore } from '@/stores/themeStore'
import { useUIStore } from '@/stores/uiStore'
import { useSearchStore } from '@/stores/searchStore'
import { useTranslation } from '@/stores/i18nStore'
import { useSearchMutation } from '@/hooks/useSearch'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  MenuIcon,
  SunIcon,
  MoonIcon,
  SearchIcon,
  XIcon,
  CheckIcon
} from '@/components/ui/icons'

export function Header() {
  const { t, locale, setLocale } = useTranslation()
  const { trackSearch, trackThemeChange, trackLanguageChange, trackSidebarToggle } = useAnalytics()
  const { toggleSidebar, apiConnected, sidebarCollapsed } = useUIStore()
  const { resolvedTheme, toggleTheme } = useThemeStore()
  const {
    searchString,
    setSearchString,
    searchOptions,
    currentFileInfo,
    currentText,
    contentSource,
    isLoading,
    clearContent,
    setShowTextViewer
  } = useSearchStore()
  const searchMutation = useSearchMutation()

  const handleSearch = () => {
    // Numbers mode doesn't require a search string
    const isNumbersMode = searchOptions.searchMode === 'numbers'
    if (!isNumbersMode && !searchString.trim()) return
    if (!currentFileInfo) return

    // Track search
    trackSearch(searchString || '[numbers]', {
      fileId: parseInt(String(currentFileInfo.fileId)) || 0,
      matchMode: searchOptions.matchTypes.join(','),
    })

    // Close text viewer when searching
    setShowTextViewer(false)

    // For pasted text or URL content, send the text directly
    // For files, send the fileId
    const isPositionMode = searchOptions.searchMode === 'position'
    const payload = {
      searchString: searchString || '',
      searchMode: searchOptions.searchMode,
      matchTypes: searchOptions.matchTypes,
      // Include numberType only if numbers search mode is enabled
      ...(isNumbersMode && searchOptions.numberType
        ? { numberType: searchOptions.numberType }
        : {}),
      // Include positionOptions if position search mode is enabled
      ...(isPositionMode
        ? { positionOptions: searchOptions.positionOptions || { skip: [], repeat: [], exact: [], derivate: false } }
        : {}),
      ...(contentSource === 'file'
        ? { fileId: currentFileInfo.fileId }
        : { text: currentText })
    }

    searchMutation.mutate(payload)
  }

  // Numbers mode can search without a search string (finds all numbers)
  const isNumbersMode = searchOptions.searchMode === 'numbers'
  const canSearch = (isNumbersMode || searchString.trim()) && currentFileInfo && !isLoading

  const toggleLanguage = () => {
    const newLocale = locale === 'en' ? 'am' : 'en'
    trackLanguageChange(newLocale, locale)
    setLocale(newLocale)
  }

  const handleToggleSidebar = () => {
    trackSidebarToggle(!sidebarCollapsed)
    toggleSidebar()
  }

  const handleToggleTheme = () => {
    trackThemeChange(resolvedTheme === 'light' ? 'dark' : 'light')
    toggleTheme()
  }

  return (
    <motion.header
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn(
        'fixed top-0 left-0 right-0 h-16 z-50',
        'bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md',
        'border-b border-zinc-200 dark:border-zinc-800'
      )}
    >
      <div className="h-full flex items-center px-4 md:px-6 gap-4">
        {/* Menu Toggle */}
        <button
          onClick={handleToggleSidebar}
          className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          aria-label="Toggle sidebar"
        >
          <MenuIcon className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-zinc-900 dark:bg-white flex items-center justify-center">
            <span className="text-sm font-bold text-white dark:text-zinc-900 font-ethiopic">ሣ</span>
          </div>
          <span className="font-semibold text-zinc-900 dark:text-white hidden sm:block">
            {t('appName')}
          </span>
        </div>

        {/* Search Bar */}
        <div className="flex-1 max-w-xl mx-auto">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSearch()
            }}
          >
            <div className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-xl',
              'bg-zinc-100 dark:bg-zinc-900',
              'border border-transparent',
              'focus-within:bg-white dark:focus-within:bg-zinc-800',
              'focus-within:border-zinc-300 dark:focus-within:border-zinc-700',
              'focus-within:shadow-sm',
              'transition-all duration-200'
            )}>
              {/* File indicator */}
              <AnimatePresence>
                {currentFileInfo && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-1.5 pr-2 border-r border-zinc-300 dark:border-zinc-700"
                  >
                    <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-xs text-zinc-600 dark:text-zinc-400 max-w-[80px] truncate hidden md:inline">
                      {currentFileInfo.filename}
                    </span>
                    <button
                      type="button"
                      onClick={clearContent}
                      className="p-0.5 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    >
                      <XIcon className="w-3 h-3 text-zinc-400" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Search icon */}
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <SearchIcon className="w-4 h-4 text-zinc-400" />
              )}

              {/* Input */}
              <input
                type="text"
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                placeholder={currentFileInfo ? t('searchPlaceholder') : t('selectFileFirst')}
                disabled={!currentFileInfo}
                className={cn(
                  'flex-1 bg-transparent text-sm',
                  'text-zinc-900 dark:text-zinc-100',
                  'placeholder:text-zinc-400',
                  'focus:outline-none',
                  'disabled:cursor-not-allowed',
                  locale === 'am' && 'font-ethiopic'
                )}
              />

              {/* Clear button */}
              {searchString && (
                <button
                  type="button"
                  onClick={() => setSearchString('')}
                  className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
                >
                  <XIcon className="w-3.5 h-3.5 text-zinc-400" />
                </button>
              )}

              {/* Search button */}
              {canSearch && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  type="submit"
                  className="px-3 py-1 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-xs font-medium hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors"
                >
                  {t('search')}
                </motion.button>
              )}
            </div>
          </form>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* API Status */}
          <div className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800">
            <span className={cn(
              'w-1.5 h-1.5 rounded-full',
              apiConnected ? 'bg-emerald-500' : 'bg-red-500'
            )} />
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              {apiConnected ? t('connected') : t('offline')}
            </span>
          </div>

          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            className="px-2 py-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-xs font-medium text-zinc-600 dark:text-zinc-400"
            aria-label="Toggle language"
          >
            <span className={locale === 'am' ? 'font-ethiopic' : ''}>
              {locale === 'en' ? 'አማ' : 'EN'}
            </span>
          </button>

          {/* Theme toggle */}
          <button
            onClick={handleToggleTheme}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Toggle theme"
          >
            {resolvedTheme === 'light' ? (
              <MoonIcon className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
            ) : (
              <SunIcon className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
            )}
          </button>

          {/* Sign in button */}
          <button className="hidden sm:flex px-4 py-2 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors">
            {t('signIn')}
          </button>
        </div>
      </div>
    </motion.header>
  )
}
